import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import React, { useCallback, useEffect, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { PREVIEW_TITLE_STYLE } from 'util/Definitions/Constants/Previews';
import { NameIdObj } from 'common/models/GenericTypes';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import {
  SubmissionCreateData,
  UiCandidateData,
  UiSubmissionEdit,
} from 'common/models/Submissions/Submission';
import { GetSubmissionMessagePreview } from 'services/SubmissionsService';

interface Props {
  open: boolean;
  body: string;
  isDarkTheme: boolean;
  candidates: NameIdObj[];
  contactAttendeesData: NameIdObj[];
  submissionData: UiSubmissionEdit;
  candidateData: UiCandidateData[];
  submissionDate: moment.Moment | null;
  loadingHandler?: (isLoading: boolean) => void;
  closeHandler: () => void;
}

export default function SubmissionPreviewDialog({
  open,
  candidates,
  body,
  submissionData,
  candidateData,
  submissionDate,
  isDarkTheme,
  contactAttendeesData,
  closeHandler,
  loadingHandler,
}: Props) {
  const [selectedCandidateId, setSelectedCandidateId] = useState(0);
  const [selectedContactId, setSelectedContactId] = useState(0);
  const [previewBody, setPreviewBody] = useState('');

  const getPreviewCallback = useCallback(async () => {
    if (submissionData) {
      let contacts: number[] = [];
      if (submissionData.contactsJson) contacts = JSON.parse(submissionData.contactsJson);

      const data: SubmissionCreateData = {
        assignedToId: submissionData.assignedTo,
        body: body,
        candidateData: candidateData,
        contacts: contacts,
        clientDistributionModeID: submissionData.clientDistributionModeId,
        jobId: submissionData.jobId,
        jobTitle: submissionData.jobTitle,
        sendCandidateConfirmation: submissionData.sendCandidateConfirmaiton,
        subject: submissionData.subject,
        submissionDate: submissionDate ? submissionDate.format('YYYY-MM-DDTHH:mm') : '',
        followUpAssignedToId: 0,
      };

      loadingHandler && loadingHandler(true);
      const res = await GetSubmissionMessagePreview(selectedCandidateId, selectedContactId, data);
      if (res) setPreviewBody(res.value);
      loadingHandler && loadingHandler(false);
    }
  }, [
    body,
    candidateData,
    loadingHandler,
    selectedCandidateId,
    selectedContactId,
    submissionData,
    submissionDate,
  ]);

  useEffect(() => {
    if (open && submissionData && selectedCandidateId && selectedContactId) {
      setPreviewBody('');
      getPreviewCallback();
    }
  }, [getPreviewCallback, open, selectedCandidateId, selectedContactId, submissionData]);

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle component="div" display="flex" sx={PREVIEW_TITLE_STYLE}>
        <Box alignSelf="center">Submission Preview</Box>
        <Box ml="auto">
          <IconButton
            size="small"
            onClick={closeHandler}
            sx={{ color: (t) => t.palette.primary.contrastText }}
          >
            <CancelIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box>
          <Box display="flex" gap={1}>
            <TextField
              select
              label="Candidate"
              value={selectedCandidateId.toString()}
              onChange={({ target }) => setSelectedCandidateId(+target.value)}
              fullWidth
              margin="normal"
            >
              <MenuItem value="0">-- Select --</MenuItem>
              {candidates.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Contact"
              value={selectedContactId.toString()}
              onChange={({ target }) => setSelectedContactId(+target.value)}
              fullWidth
              margin="normal"
            >
              <MenuItem value="0">-- Select --</MenuItem>
              {contactAttendeesData.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Editor
            tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce-5.10.2/tinymce.min.js'}
            value={previewBody}
            disabled={true}
            init={{
              height: 420,
              skin: isDarkTheme ? 'oxide-dark' : undefined,
              content_css: isDarkTheme ? 'dark' : undefined,
              branding: false,
              menubar: false,
              statusbar: false,
              contextmenu: false,
              resize: false,
              plugins: 'powerpaste link code',
              browser_spellcheck: true,
              verify_html: false,
              toolbar1: '',
              readonly: true,
            }}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
}
